import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getSyncScrolling } from 'store';
import {
  AllDocumentTypes,
  PageWidthFit,
  ZoomLevelLimits,
  OPPOSITE_DOCUMENT,
  ReportDocumentTypes,
  PDFTRON_DEFAULT_TOOL,
  PDFTronTools,
  DocumentTypes,
} from 'types';
import { PDFManagerFactory } from 'pdftron';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import { updatePreviousScrollPostion } from 'components/common/GVDocumentsTopBar/SyncScrollingButton';
import { useZoomStateStore } from 'zstore/zoomStateStore';
import { trackEvent } from 'components/Tracker/TrackerProvider';
import { useDiffZoomStore } from 'zstore/diffZoomStore';

const ESC_KEY = 'Escape';
enum ZoomMethod {
  Mousewheel = 'mousewheel',
  UiControl = 'ui-control',
  Marquee = 'marquee',
  Resize = 'resize',
  Programmatic = 'programmatic',
}
const usePDFZoom = (documentType: AllDocumentTypes) => {
  const webViewer = PDFManagerFactory.getViewer(documentType);
  const iframeWindow = webViewer?.getIframeWindow();
  const {
    zoomChanging,
    setZoomChanging,
    zoomOption: option,
    setZoomOption,
    zoomLockLevel: lockLevel,
    setZoomLockLevel,
  } = useZoomStateStore();
  const zoomOption = option[documentType];
  const zoomLockLevel = lockLevel[documentType];
  const syncScrolling = useSelector(getSyncScrolling);
  const scrollElementSource = PDFTronManager.getScrollElement(DocumentTypes.source);
  const scrollElementTarget = PDFTronManager.getScrollElement(DocumentTypes.target);

  const [marqueeSelected, setMarqueeSelected] = useState(false);
  const [documentZoom, setDocumentZoom] = useState(webViewer?.getZoom() || ZoomLevelLimits.defaultZoom * 100);
  const [marqueePreviousTool, setMarqueePreviousTool] = useState(PDFTRON_DEFAULT_TOOL);

  const getZoom = () => {
    return webViewer?.getZoom() ?? ZoomLevelLimits.defaultZoom * 100;
  };

  const handleDocumentZoom = (
    newZoom: { zoomLevel: number; zoomPosition?: { x: number; y: number } },
    pageWidthFit?: PageWidthFit,
    method?: ZoomMethod,
  ) => {
    const previousZoomLevel = webViewer?.getPreviousZoomLevel?.();
    if (syncScrolling && webViewer && documentType !== ReportDocumentTypes.report) {
      const oppositeDocument = OPPOSITE_DOCUMENT[documentType];
      const otherViewer = PDFManagerFactory.getViewer(oppositeDocument);
      if (otherViewer) {
        let otherZoomLevel;
        if (pageWidthFit) {
          otherViewer.changeFitMode(pageWidthFit);
          setZoomOption(oppositeDocument, pageWidthFit || '');
        } else {
          const zoomDiff = newZoom.zoomLevel - webViewer.getPreviousZoomLevel();
          otherZoomLevel = otherViewer.getPreviousZoomLevel() + zoomDiff;
          // Don't allow zooming out to negative values
          if (otherZoomLevel < ZoomLevelLimits.minZoom * 100) {
            otherZoomLevel = ZoomLevelLimits.minZoom * 100;
          }
          otherViewer.changeZoom(otherZoomLevel, newZoom.zoomPosition);
          setZoomChanging();
          setZoomOption(oppositeDocument, '');
        }

        updateZoomLockLevel(pageWidthFit || (otherZoomLevel as number), true); // update opposite doc zoom lock level
      }
    }

    setDocumentZoom(newZoom.zoomLevel);
    if (!pageWidthFit) webViewer?.setPreviousZoomLevel(newZoom.zoomLevel);
    updateZoomLockLevel(pageWidthFit || newZoom.zoomLevel); // update current doc zoom lock level

    // Track: zoom within 3s of a difference selection (one-shot per selection)
    if (useDiffZoomStore.getState().consumeIfWithin(3000)) {
      trackEvent({
        name: 'difference-zoom-after-select',
        data: {
          differenceId: useDiffZoomStore.getState().getLastSelectionId(),
          newZoomLevel: newZoom.zoomLevel,
          previousZoomLevel,
          method: method || ZoomMethod.Programmatic,
        },
      });
    }
  };

  const toggleMarquee = () => {
    // reset selected tool (for annotation controls.)
    setZoomOption(documentType, '');

    if (!marqueeSelected) {
      // if we are enabling marquee zoom we need to store the previously used tool
      // we need to use the instance instead of the value in redux because it can be different for both documents depending on the tool's flow
      const instanceTool = webViewer?.instance.UI.getToolMode();
      if (instanceTool) {
        setMarqueePreviousTool(instanceTool.name as PDFTronTools);
      }
    }

    setMarqueeSelected(!marqueeSelected);
    webViewer?.toggleMarqueeTool(!marqueeSelected, marqueePreviousTool);
  };

  useEffect(() => {
    webViewer?.setMaxZoomLevel(ZoomLevelLimits.maxZoom);
    webViewer?.setMinZoomLevel(ZoomLevelLimits.minZoom);
    webViewer?.setPreviousZoomLevel(getZoom());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateZoomWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (marqueeSelected) {
          toggleMarquee();
        }
        if (!zoomLockLevel) {
          if (zoomOption !== '') {
            setZoomOption(documentType, '');
          }
          const newZoom = getZoom();
          handleDocumentZoom(
            { zoomLevel: newZoom, zoomPosition: { x: e.clientX, y: e.clientY } },
            undefined,
            ZoomMethod.Mousewheel,
          );
        }
      }
    };

    if (iframeWindow) {
      iframeWindow.addEventListener('wheel', updateZoomWheel);
    }

    return () => {
      if (iframeWindow) {
        iframeWindow.removeEventListener('wheel', updateZoomWheel);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncScrolling]);

  useEffect(() => {
    const updateResizeZoom = () => {
      const zoomLevel = getZoom();
      if (zoomLevel !== webViewer?.getPreviousZoomLevel()) {
        handleDocumentZoom({ zoomLevel }, undefined, ZoomMethod.Resize);
      }
    };

    if (iframeWindow) {
      iframeWindow.addEventListener('resize', updateResizeZoom);
    }

    return () => {
      if (iframeWindow) {
        iframeWindow.removeEventListener('resize', updateResizeZoom);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncScrolling]);

  // Listening to esc key to change pdftron zoom tool
  useEffect(() => {
    const resetZoomTool = (e: KeyboardEvent) => {
      if (marqueeSelected) {
        if (e.key === ESC_KEY) {
          toggleMarquee();
        }
      }
    };

    if (window && iframeWindow) {
      // setting event listener in windows too, otherwise user needs to have clicked the iframe to be able to use esc to remove marquee
      window.addEventListener('keyup', resetZoomTool);
      iframeWindow.addEventListener('keyup', resetZoomTool);
    }

    return () => {
      if (window && iframeWindow) {
        window.removeEventListener('keyup', resetZoomTool);
        iframeWindow.removeEventListener('keyup', resetZoomTool);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marqueeSelected]);

  useEffect(() => {
    const updateZoom = () => {
      if (marqueeSelected) {
        const zoomLevel = getZoom();
        handleDocumentZoom({ zoomLevel }, undefined, ZoomMethod.Marquee);
      }
    };

    if (iframeWindow) {
      iframeWindow.addEventListener('mouseup', updateZoom);
    }

    return () => {
      if (iframeWindow) {
        iframeWindow.removeEventListener('mouseup', updateZoom);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marqueeSelected]);

  useEffect(() => {
    if (marqueeSelected) {
      toggleMarquee();
    }
    if (!zoomLockLevel) {
      if (zoomOption !== '' && documentZoom !== getZoom()) {
        setZoomOption(documentType, '');
      }
    }
    setDocumentZoom(getZoom());
    updatePreviousScrollPostion(scrollElementSource, scrollElementTarget);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomChanging]);

  const changeDocumentPageFitMode = (pageWidthFit: PageWidthFit) => {
    if (marqueeSelected) {
      toggleMarquee();
    }
    webViewer?.changeFitMode(pageWidthFit);
    setZoomOption(documentType, pageWidthFit);
    const zoomLevel = getZoom();
    handleDocumentZoom({ zoomLevel }, pageWidthFit, ZoomMethod.UiControl);
    updateZoomLockLevel(pageWidthFit);
  };

  const changeDocumentZoom = (zoomLevel: number) => {
    // we still need to manually check the constraint of the zoomLevel because setZoomLevel does not comply to the webviewer's zoom limit
    if (zoomLevel >= ZoomLevelLimits.minZoom * 100 && zoomLevel <= ZoomLevelLimits.maxZoom * 100) {
      if (marqueeSelected) {
        toggleMarquee();
      }

      handleDocumentZoom({ zoomLevel }, undefined, ZoomMethod.UiControl);
      webViewer?.changeZoom(zoomLevel);

      if (zoomOption !== '') {
        setZoomOption(documentType, '');
      }
    }
    updateZoomLockLevel(zoomLevel);
  };

  // If user changes zoom level while locked
  const updateZoomLockLevel = (zoomLevel: number | PageWidthFit | null, oppositeDoc?: boolean) => {
    const document = oppositeDoc ? OPPOSITE_DOCUMENT[documentType] : documentType;
    // Update Zoom Lock Level if currently locked (not null)
    if (zoomLockLevel && zoomLockLevel !== zoomLevel) {
      setZoomLockLevel(document, zoomLevel);
    }
  };

  return {
    getZoom,
    changeDocumentZoom,
    changeDocumentPageFitMode,
    toggleMarquee,
    documentZoom,
    zoomOption,
    zoomLockLevel,
    marqueeSelected,
  };
};

export default usePDFZoom;
